import { Type, type Static } from "@sinclair/typebox";

/**
 * Live trip surfaces outside the app: the iOS Live Activity (Lock Screen, Dynamic Island) and the
 * Android live notification (Android 16 Live Update, an ongoing progress notification before).
 * The apps update them while they run; the `live-trip` worker pushes the same state when they don't
 * (APNs `liveactivity` pushes, FCM `trip.live` data messages), so both must build it the same way.
 *
 * Times are Unix seconds (not ISO strings): ActivityKit decodes `content-state` with its own decoder.
 */

/** Where the trip stands, one step of the track: searching → to_pickup → at_pickup → on_trip → end. */
export const LiveTripPhase = Type.Union(
  [
    Type.Literal("searching"),
    Type.Literal("to_pickup"),
    Type.Literal("at_pickup"),
    Type.Literal("on_trip"),
    Type.Literal("completed"),
    Type.Literal("cancelled"),
    Type.Literal("no_driver"),
  ],
  { $id: "LiveTripPhase" },
);
export type LiveTripPhase = Static<typeof LiveTripPhase>;

/** The driver as the rider's live surfaces show them. */
export const LiveTripDriver = Type.Object(
  {
    name: Type.String(),
    rating: Type.Number(),
    model: Type.String(),
    color: Type.String(),
    plate: Type.String(),
  },
  { $id: "LiveTripDriver" },
);
export type LiveTripDriver = Static<typeof LiveTripDriver>;

/** What changes during the trip (ActivityKit `ContentState`). */
export const LiveTripState = Type.Object(
  {
    phase: LiveTripPhase,
    /** Unix seconds the car reaches the pickup (to_pickup) or the destination (on_trip). */
    etaAt: Type.Optional(Type.Integer()),
    /** 0…1 along the current leg: time since it began over time until `etaAt`. */
    progress: Type.Optional(Type.Number({ minimum: 0, maximum: 1 })),
    /** Unix seconds the free wait ends (at_pickup). */
    waitEndsAt: Type.Optional(Type.Integer()),
    /** Rider only, once a driver accepted. */
    driver: Type.Optional(LiveTripDriver),
    cancelledBy: Type.Optional(Type.Union([Type.Literal("rider"), Type.Literal("driver"), Type.Literal("system"), Type.Literal("staff")])),
    /** on_trip with paradas: the next stop's title and how many are left. */
    nextStop: Type.Optional(Type.String()),
    stopsRemaining: Type.Optional(Type.Integer({ minimum: 0 })),
    /** Unix seconds this state was computed (ActivityKit orders and staleness from it). */
    updatedAt: Type.Integer(),
  },
  { $id: "LiveTripState" },
);
export type LiveTripState = Static<typeof LiveTripState>;

/** What stays the same for the whole trip (ActivityKit `ActivityAttributes`). */
export const LiveTripAttributes = Type.Object(
  {
    tripId: Type.String(),
    role: Type.Union([Type.Literal("rider"), Type.Literal("driver")]),
    kind: Type.Union([Type.Literal("ride"), Type.Literal("delivery")]),
    category: Type.String(),
    /** "car" or "motorcycle": the tracker's drawing. Absent from older servers ("moto" category = motorcycle). */
    vehicleKind: Type.Optional(Type.String()),
    pickupTitle: Type.String(),
    dropoffTitle: Type.String(),
    /** Rider rides only: the four digits told to the driver. */
    boardingCode: Type.Optional(Type.String()),
    /** Driver only: who is being picked up. */
    riderName: Type.Optional(Type.String()),
    /** Rider: the fare paid. Driver: what they receive. */
    amountCents: Type.Integer(),
  },
  { $id: "LiveTripAttributes" },
);
export type LiveTripAttributes = Static<typeof LiveTripAttributes>;

/**
 * `PUT /trips/{id}/live-activity` — the push token of this trip's Live Activity (iOS). The worker
 * sends `liveactivity` pushes to it until the trip ends or the app ends the activity
 * (`DELETE /trips/{id}/live-activity`).
 */
export const LiveActivityTokenInput = Type.Object(
  {
    app: Type.Union([Type.Literal("rider"), Type.Literal("driver")]),
    token: Type.String({ minLength: 8, maxLength: 4096 }),
    environment: Type.Optional(Type.Union([Type.Literal("sandbox"), Type.Literal("production")])),
  },
  { $id: "LiveActivityTokenInput" },
);
export type LiveActivityTokenInput = Static<typeof LiveActivityTokenInput>;

/** Leg boundaries on the shared track (Android ProgressStyle segments, the iOS track): pickup leg, then the ride. */
export const LIVE_TRACK = { pickupLeg: 40, ride: 60 } as const;
